"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Card, CardContent } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import Image from "next/image"
import { cn } from "@/lib/utils"
import { Languages, Briefcase, GraduationCap, Users, Heart, Sparkles, ArrowRight } from "lucide-react"
import { servicesTravel } from "@/data/services"
import { useI18n } from "@/context/i18n"
import { pickLocalized } from "@/lib/i18nUtils"

// This component expects a `service` prop to select which items to show.
// It defensively guards against missing/empty items and out-of-range indices.

type ServiceKey = "lets-study" | "lets-travel" | "participaciones" | "lets-participaciones" | "others" | "lets-inmigrate"

type StepItem = {
  key: string
  title: string
  desc?: string
  image?: string
  ctaLabel?: string
  href?: string
}

interface ServicesStepperProps { service: ServiceKey }

// Example data lookup: original defaults for study/travel
const DEFAULT_ITEMS: Record<ServiceKey, StepItem[]> = {
  "lets-study": [
    { key: "idiomas", title: "Idiomas", image: undefined },
    { key: "prep", title: "Preparación", image: undefined },
  ],
  "lets-travel": [
    { key: "turismo", title: "Turismo", image: undefined },
    { key: "summer", title: "Summer", image: undefined },
  ],
  "lets-inmigrate": [],
  participaciones: [],
  "lets-participaciones": [],
  others: [],
}

export default function ServicesStepper({ service }: ServicesStepperProps) {
  const { t, lang } = useI18n()
  // Resolve items based on service; fallback to empty array.
  const items = useMemo<StepItem[]>(() => {
    // Prefer explicit dataset mapping to restore previous content
    if (service === "lets-study") {
      return servicesTravel.map((s) => ({
        key: s.key,
        title: pickLocalized(lang, s.title, s.title_en, s.title_fr),
        desc: pickLocalized(lang, s.description, s.description_en, s.description_fr),
        image: s.image,
        href: s.href,
        ctaLabel: pickLocalized(lang, s.buttonText ?? t('btn_see_more'), s.buttonText_en ?? t('btn_see_more'), s.buttonText_fr ?? t('btn_see_more')),
      }))
    }
    if (service === "lets-travel") {
      // Viajes: visas, internacionales/nacionales y categorías especiales
      const TRAVEL_ITEMS: StepItem[] = [
        { key: "visa-americana", title: "Visa Americana", desc: "Asesoría y gestión para tu visa de EE. UU.", image: "https://elbajio.mx/wp/wp-content/uploads/Precio-de-la-visa-americana.jpg", href: "/servicios/visa-americana", ctaLabel: "Ver más" },
        { key: "visa-canadiense", title: "Visa Canadiense", desc: "Asesoría y gestión para tu visa canadiense.", image: "https://cdn-3.expansion.mx/dims4/default/9940267/2147483647/strip/true/crop/1269x826+0+0/resize/3600x2344!/format/webp/quality/80/?url=https:%2F%2Fcdn-3.expansion.mx%2F27%2F78%2F6ed2805b47248aa8046e2f76fcb8%2Frequisitos-para-visa-canadiense.jpg", href: "/servicios/visa-canadiense", ctaLabel: "Ver más" },
        { key: "internacionales", title: "Viajes internacionales", desc: "Explora el mundo con experiencias únicas y seguras.", image: "https://drive.google.com/uc?export=view&id=1AJGWIy4nCM5umdbaLfW5cT3715L1O4qT", href: "servicios/viajes-internacionales", ctaLabel: "Ver más" },
        { key: "nacionales", title: "Viajes nacionales", desc: "Descubre destinos dentro del país con itinerarios a tu medida.", image: "https://drive.google.com/uc?export=view&id=5ED70424-C737-4A8D-9D46-5397E045AB3B", href: "/servicios/viajes-nacionales", ctaLabel: "Ver más" },
        { key: "despedida-de-solteros", title: "Despedida de solteros", desc: "La mejor despedida con amigos en el destino perfecto.", image: "https://drive.google.com/uc?export=view&id=1ruzXrjcxO8ujGHYE8OeHULDSttzf2Wul", href: "/servicios/despedida-de-solteros", ctaLabel: "Ver más" },
        { key: "lunas-de-miel", title: "Lunas de Miel", desc: "Destinos románticos y experiencias únicas para recién casados.", image: "https://drive.google.com/uc?export=view&id=1XsbwBXuw6afNxn5xH-CbM1KMR9e3zlsd", href: "/servicios/lunas-de-miel", ctaLabel: "Ver más" },
        { key: "cruceros", title: "Cruceros", desc: "Vive el mar con rutas y cruceros inolvidables.", image: "https://drive.google.com/uc?export=view&id=1HL7EokRwA6lCTynG_1gAVWyB-pA_88Kk", href: "/servicios/cruceros", ctaLabel: "Ver más" },
        { key: "quinceanieras", title: "Quinceañeras", desc: "Celebra tus XV con un viaje inolvidable.", image: "https://drive.google.com/uc?export=view&id=16pwcUEnVAcfXGiPEhQr9Ln55a7HTTwgd", href: "/servicios/quinceanieras", ctaLabel: "Ver más" },
        { key: "bodas-destino", title: "Bodas", desc: "Organizamos tu boda en el destino de tus sueños.", image: "https://drive.google.com/uc?export=view&id=10i7X3QAmb4oyhZUBHfxt0TwkYfUlpcVL", href: "/servicios/bodas", ctaLabel: "Ver más" },
        { key: "viajes-personalizados", title: "Viajes Personalizados", desc: "Armamos tu viaje tal como lo imaginas.", image: "https://drive.google.com/uc?export=view&id=16Sw5fmDe2RU3XRHY2XQKSJy7-7lpKHaj", href: "/servicios/viajes-personalizados", ctaLabel: "Ver más" },
      ]
      // ensure ctaLabel and text localize with current language
      return TRAVEL_ITEMS.map(item => ({
        ...item,
        title: lang === 'EN' ? (
          item.key === 'visa-americana' ? 'US Visa' :
          item.key === 'visa-canadiense' ? 'Canadian Visa' :
          item.key === 'internacionales' ? 'International travel' :
          item.key === 'nacionales' ? 'Domestic travel' :
          item.key === 'despedida-de-solteros' ? 'Bachelor parties' :
          item.key === 'lunas-de-miel' ? 'Honeymoons' :
          item.key === 'cruceros' ? 'Cruises' :
          item.key === 'quinceanieras' ? 'Quinceañeras' :
          item.key === 'bodas-destino' ? 'Destination weddings' :
          item.key === 'viajes-personalizados' ? 'Custom trips' : item.title
        ) : item.title,
        desc: lang === 'EN' ? (
          item.key === 'visa-americana' ? 'Advisory and processing for your U.S. visa.' :
          item.key === 'visa-canadiense' ? 'Advisory and processing for your Canadian visa.' :
          item.key === 'internacionales' ? 'Explore the world with unique and safe experiences.' :
          item.key === 'nacionales' ? 'Discover destinations within the country with tailor-made itineraries.' :
          item.key === 'despedida-de-solteros' ? 'The best bachelor party with friends at the perfect destination.' :
          item.key === 'lunas-de-miel' ? 'Romantic destinations and unique experiences for newlyweds.' :
          item.key === 'cruceros' ? 'Enjoy the sea with unforgettable cruise routes.' :
          item.key === 'quinceanieras' ? 'Celebrate your XV with an unforgettable trip.' :
          item.key === 'bodas-destino' ? 'We organize your wedding in the destination of your dreams.' :
          item.key === 'viajes-personalizados' ? 'We craft your trip exactly as you imagine it.' : item.desc
        ) : item.desc,
        ctaLabel: lang === 'EN' ? (item.ctaLabel ?? t('btn_see_more')) : (item.ctaLabel ?? t('btn_see_more')),
      }))
    }
  if (service === "participaciones" || service === "lets-participaciones") {
      // Participaciones: viva australia, verifica y viaja, ferias, expo mexico mujer
      const PARTICIPACIONES_ITEMS: StepItem[] = [
        { key: "expo-mexico-mujer", title: "Expo México Mujer", desc: "Evento enfocado en empoderamiento y oportunidades.", image: "https://expomexico.ca/wp-content/uploads/2025/04/professional-traditional-dancer-looking-camera-with-blur-background-aig43-2048x1148.jpg", href: "https://expomexico.ca/", ctaLabel: "Ver más" },
        { key: "viva-australia", title: "VIVA Australia", desc: "Programa y eventos enfocados en estudiar y vivir en Australia.", image: "https://yt3.googleusercontent.com/1GbRfJLbdBIUdNVif7Sow5mn6Y9VTVPKvC--aaAf7chPr02nDxZulPhevd57ViT3qLElCiWf8g=s900-c-k-c0x00ffffff-no-rj", href: "/categoria/participaciones/australia", ctaLabel: "Ver más" },
        { key: "verifica-y-viaja", title: "Verifica y Viaja", desc: "Iniciativa para viajar con confianza y respaldo.", image: "https://tse1.mm.bing.net/th/id/OIP._KOBct6-L9lQDQJ3VpTDfgHaDy?cb=ucfimg2&ucfimg=1&rs=1&pid=ImgDetMain&o=7&rm=3", href: "/categoria/participaciones/verifica-y-viaja", ctaLabel: "Ver más" },
        { key: "comce" , title: "COMCE", desc: "Participación activa en el Consejo Empresarial Mexicano de Comercio Exterior.", image: "https://educacioncontinua.anahuac.mx/cdn/shop/files/Logo-COMCE-2024_535x.png?v=1741811543", href: "/categoria/participaciones/comce", ctaLabel: "Ver más" },
      ]
      return PARTICIPACIONES_ITEMS.map(item => ({
        ...item,
        title: lang === 'EN' ? (
          item.key === 'expo-mexico-mujer' ? 'Expo Mexico Woman' :
          item.key === 'viva-australia' ? 'VIVA Australia' :
          item.key === 'verifica-y-viaja' ? 'Verify & Travel' : 
          item.key === 'COMCE' ? 'COMCE' : item.title
        ) : item.title,
        desc: lang === 'EN' ? (
          item.key === 'expo-mexico-mujer' ? 'Event focused on empowerment and opportunities.' :
          item.key === 'viva-australia' ? 'Programs and events focused on studying and living in Australia.' :
          item.key === 'verifica-y-viaja' ? 'Initiative to travel with confidence and support.' :
          item.key === 'comce' ? 'Active participation in the Mexican Business Council for Foreign Trade.' : item.desc
        ) : item.desc,
        ctaLabel: lang === 'EN' ? (item.ctaLabel ?? t('btn_see_more')) : (item.ctaLabel ?? t('btn_see_more')),
      }))
    }
    // Fallbacks (window injection) to not break any custom runtime wiring
    try {
      const w = typeof window !== "undefined" ? (window as any) : undefined
      const external = w?.__LET_STEPPER__?.[service]
      if (Array.isArray(external)) return external
    } catch {}
    return DEFAULT_ITEMS[service] ?? []
  }, [service, lang, t])

  const safeLength = items?.length ?? 0
  const [activeIdx, setActiveIdx] = useState<number>(0)

  // Reset active index on service change and clamp within bounds.
  useEffect(() => {
    setActiveIdx(0)
  }, [service])

  // Clamp when items change.
  useEffect(() => {
    if (activeIdx >= safeLength) setActiveIdx(0)
  }, [safeLength, activeIdx])

  const hasItems = safeLength > 0
  const active = hasItems ? items[activeIdx] : undefined

  // Original behavior: show a neutral placeholder when no data
    if (!hasItems) {
    return (
      <div className="w-full border border-dashed border-gray-300 p-6 text-center text-gray-500">
        {t('services_no_subservices')}
      </div>
    )
  }

  const fallbackImages: Record<string, string> = {
    idiomas: "https://images.unsplash.com/photo-1513258496099-48168024aec0?q=80&w=1600&auto=format&fit=crop",
    turismo: "https://images.unsplash.com/photo-1526778548025-fa2f459cd5c1?q=80&w=1600&auto=format&fit=crop",
    summer: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?q=80&w=1600&auto=format&fit=crop",
    prep: "https://images.unsplash.com/photo-1503676260728-1c00da094a0b?q=80&w=1600&auto=format&fit=crop",
  }

  const imgSrc = active?.image || fallbackImages[active?.key || "idiomas"]

  // Map keys to icons for a more evocative visual style
  const iconFor = (key?: string) => {
    switch (key) {
      case "idiomas":
        return Languages
      case "work-and-study":
        return Briefcase
      case "educacion-superior":
        return GraduationCap
      case "highschool":
        return GraduationCap
      case "aupair":
        return Heart
      default:
        return Sparkles
    }
  }

  return (
  <div className="grid md:grid-cols-2 gap-10" data-tour="services-stepper">
      {/* Step selector - minimal, elegant */}
      <div className="space-y-3">
        {items.map((it, idx) => {
          const Icon = iconFor(it.key)
          const selected = idx === activeIdx
          return (
            <button
              key={it.key}
              className={cn(
                "relative w-full text-left p-4 md:p-5 rounded-xl transition-all group",
                "bg-white", // keep white even on hover
                selected ? "shadow-sm" : ""
              )}
              onClick={() => {
                setActiveIdx(idx)
                if (typeof window !== "undefined") {
                  window.dispatchEvent(new CustomEvent('home-tour:select-service', { detail: { key: it.key } }))
                }
              }}
            >
              {/* animated top border: expands on hover, stays full when selected */}
              <span className={cn(
                "absolute top-0 left-0 h-[3px] bg-primary transition-all duration-300",
                selected ? "w-full" : "w-0 group-hover:w-[50%]"
              )} />

              <div className="flex items-start gap-4">
                {/* icon only, no gray background */}
                <Icon className={cn("h-5 w-5 mt-1", selected ? "text-primary" : "text-gray-700")}/>
                <div className="min-w-0">
                  <div className="font-bold text-black leading-snug" style={{ fontFamily: 'DIN, sans-serif' }}>{it.title}</div>
                  {it.desc && (
                    <div className="text-sm text-gray-600 mt-0.5" style={{ fontFamily: 'Montserrat, sans-serif' }}>{it.desc}</div>
                  )}
                </div>
              </div>
            </button>
          )
        })}
      </div>

      {/* Active preview - smaller image, fully visible */}
      <Card className="overflow-hidden border border-gray-200 shadow-sm rounded-xl">
        <div className="p-3">
      <div className="relative h-72 w-full rounded-lg overflow-hidden border border-gray-200 bg-white">
            {imgSrc && (
        <Image src={imgSrc} alt={active?.title || ""} fill className="object-cover" />
            )}
          </div>
        </div>
        <CardContent className="pt-0 pb-6 px-6">
          {active?.title && (
            <h3 className="text-xl font-bold mt-2" style={{ fontFamily: 'DIN, sans-serif' }}>{active.title}</h3>
          )}
          {active?.desc && (
            <p className="text-gray-700 mt-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>{active.desc}</p>
          )}
          {active?.href && (
            <div className="mt-5">
              <Button
                asChild
                variant="outline"
                className="border-gray-300 text-black hover:bg-black hover:text-white rounded-lg px-5 py-2.5"
                data-tour="service-view-more"
              >
                <a
                  href={active.href}
                  className="inline-flex items-center"
                  onClick={() => {
                    if (typeof window !== "undefined") {
                      window.dispatchEvent(new CustomEvent('home-tour:view-more', { detail: { href: active.href } }))
                    }
                  }}
                >
                  {active?.ctaLabel ?? pickLocalized(lang, "Ver más", "See more", "Voir plus")}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </a>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
